import { Zap, Lock, Fingerprint, MapPin, KeyRound, ShieldCheck, Rewind } from "lucide-react";
import SituationPage from "@/components/site/SituationPage";
import { useMeta } from "@/hooks/useMeta";

/* "Sealed at capture" indicator, different from the other two mini-demos */
function SealedIndicatorMock() {
  return (
    <div className="mx-auto lg:mx-0 max-w-sm rounded-2xl overflow-hidden shadow-sm">
      <div className="bg-[color:var(--yn-ink)] p-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex h-2.5 w-2.5 rounded-full animate-pulse"
              style={{ background: "#ef4444" }}
              aria-hidden="true"
            />
            <span className="text-xs font-semibold uppercase tracking-wider text-white/90">
              Recording · 00:47
            </span>
          </div>
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-white/70">
            <Lock size={12} />
            Sealed at capture
          </span>
        </div>

        {/* Waveform */}
        <div className="mt-6 flex items-center gap-1 h-14">
          {Array.from({ length: 42 }).map((_, i) => {
            const h = 20 + Math.abs(Math.sin(i * 1.7)) * 60;
            return (
              <span
                key={i}
                className="w-1 rounded-full"
                style={{
                  height: `${h}%`,
                  background: i % 4 === 0 ? "var(--yn-primary)" : "rgba(255,255,255,0.5)",
                }}
              />
            );
          })}
        </div>

        <dl className="mt-6 grid grid-cols-2 gap-4 text-[11px]">
          <div>
            <dt className="text-white/50 uppercase tracking-wider">Started</dt>
            <dd className="mt-0.5 text-white/90 font-medium">10:32:14 PM</dd>
          </div>
          <div>
            <dt className="text-white/50 uppercase tracking-wider">Location</dt>
            <dd className="mt-0.5 text-white/90 font-medium">33.3062° N</dd>
          </div>
        </dl>
      </div>
      <div className="bg-white p-4 border border-t-0 border-[color:var(--yn-border)]">
        <p className="text-[11px] leading-relaxed text-[color:var(--yn-muted)]">
          Encrypted before the recording ends. Fingerprinted before the
          conversation does.
        </p>
      </div>
    </div>
  );
}

export default function Altercations() {
  useMeta({
    title: "App that records altercations for evidence, YeboNadi",
    description:
      "Domestic violence, abuse, doorstep confrontations, road incidents, moments that escalate in seconds. YeboNadi captures with one tap, seals audio + time + GPS at the moment of capture, and preserves the record before anyone can rewrite it.",
    path: "/altercations",
  });

  return (
    <SituationPage
      slug="altercations"
      kicker="Altercations"
      headline="When it turns, the record already exists."
      narrative={[
        "A confrontation does not warn you, and domestic violence does not announce its commencement. A voice sharpens, a door opens the wrong way, a driver gets out of the car. There is no time to unlock your phone and frame the shot. There is barely time to take a breath.",
        "You start recording manually; the app captures audio, timestamp, and GPS until you end the session. Because YeboNadi is already running, the moment can be captured while it is still happening, and sealed before it ends. Every sample is encrypted at capture. Every capture carries an embedded fingerprint, time, and location.",
        "That means the record of what actually happened exists before anyone else has a chance to shape it. It is not a viral clip. It is not a screenshot. It is a sealed, verifiable file that will still be here in the morning.",
      ]}
      miniDemo={<SealedIndicatorMock />}
      coversHeading="How YeboNadi covers this"
      covers={[
        {
          icon: Zap,
          title: "One tap to start",
          body: "Recording is triggered manually by you, a single tap, no unlock ceremony, no framing. The point is to make capture faster than escalation.",
        },
        {
          icon: Fingerprint,
          title: "Sealed while it's still happening",
          body: "Encrypted (AES-256) and hash-sealed (SHA-256) at the moment of capture. Nothing you save can be edited after it's sealed.",
        },
        {
          icon: MapPin,
          title: "Time and GPS embedded",
          body: "The exact timestamp and location are written into each sealed recording, so the where and the when are part of the file, not a separate log that can be lost.",
        },
        {
          icon: KeyRound,
          title: "Released only by legal order or signed release",
          body: "Nobody, including you, can play a sealed recording inside the app. When it becomes evidence, an authorized party submits a legal order or a signed release along with your encryption/decryption key. YeboNadi decrypts and delivers via secure cloud storage. A retrieval fee applies.",
        },
        {
          icon: ShieldCheck,
          title: "Off your phone almost immediately",
          body: "Recordings batch-upload to encrypted cloud storage, so even if the phone is broken, seized, or taken during the incident, the sealed record has already left the device.",
        },
      ]}
      subsectionTitle="Afterwards."
      subsectionBody={
        <>
          <p>
            The other side&apos;s version will be cleaner. Calmer. It will arrive
            first, because whoever is not shaking usually tells the story
            first. That is not a flaw in you. That is how confrontations work.
          </p>
          <p>
            Yours will be sealed. Timestamped. Located. Not a memory of what
            was said, an encrypted file that carries a fingerprint anyone can
            verify. When it becomes evidence, with a lawyer, an officer, HR,
            or a domestic-violence advocate, your representative submits a
            legal order or a signed release along with your
            encryption/decryption key. YeboNadi decrypts the record and
            delivers it via secure cloud storage. A retrieval fee applies.
          </p>
          <p className="flex items-center gap-2 text-[color:var(--yn-ink)] font-medium">
            <Rewind size={18} style={{ color: "var(--yn-primary)" }} />
            You do not have to remember perfectly. The record already does.
          </p>
        </>
      }
      footerNote={
        <>
          If you are in immediate danger, contact local emergency services
          first. In the US call 911. If you are experiencing domestic violence,
          the National Domestic Violence Hotline is available 24/7 at
          1-800-799-7233.
        </>
      }
    />
  );
}
